import { useState, useEffect, useCallback } from 'react';
import { UserCog, Phone, UserPlus, Activity, MessageSquare } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { leadService } from '../../services/api';
import { Skeleton, EmptyState, Badge, Button, Tab } from '../../components/ui';
import { STATUS_COLORS, STATUS_NAMES, PRIORITIES } from '../../constants';
import LeadProgressPath from './LeadProgressPath';
import AddNoteCard from './AddNoteCard';
import AssignLeadModal from './AssignLeadModal';

const LeadDetailView = ({ leadId, onCall }) => {
  const { dark } = useTheme();
  const { user, hasPermission, hasRole } = useAuth();
  const canAssign = hasPermission('leads', 'assign');
  const [tab, setTab] = useState('info');
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(false);

  const fetchDetails = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await leadService.getById(leadId);
      setDetails(data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { fetchDetails(); }, [fetchDetails]);

  if (loading) return <Skeleton rows={6} cols={2} />;
  if (!details) return <EmptyState title="Lead not found" />;

  const { lead, statusHistory, timeline, callHistory, followUps } = details;
  const isOwner = lead.assignedCounsellor?._id === user?._id;
  const canAddNote = hasPermission('leads', 'update') && (!hasRole('COUNSELLOR') || isOwner);

  const lbl = (l, v) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: dark ? '#64748B' : '#94A3B8', fontWeight: 600, marginBottom: 2 }}>{l}</div>
      <div style={{ fontSize: 13, color: dark ? '#F1F5F9' : '#0F172A' }}>{v || '—'}</div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Badge color={lead.currentStatus?.color || STATUS_COLORS[lead.statusCode]}>{lead.currentStatus?.name || STATUS_NAMES[lead.statusCode]}</Badge>
          <Badge color={PRIORITIES[lead.priority]} variant="outline">{lead.priority}</Badge>
        </div>
        <Button size="sm" variant="success" icon={Phone} onClick={onCall}>Call</Button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, padding: '10px 14px', borderRadius: 10, background: dark ? '#1E293B' : '#F8FAFC', border: `1px solid ${dark ? '#334155' : '#E2E8F0'}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <UserCog size={14} color={dark ? '#94A3B8' : '#64748B'} />
          <span style={{ fontSize: 12, color: dark ? '#94A3B8' : '#64748B' }}>Counsellor:</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: dark ? '#F1F5F9' : '#0F172A' }}>
            {lead.assignedCounsellor ? `${lead.assignedCounsellor.firstName} ${lead.assignedCounsellor.lastName || ''}` : 'Unassigned'}
          </span>
        </div>
        {canAssign && (
          <Button size="sm" variant="outline" icon={UserPlus} onClick={() => setShowAssign(true)}>
            {lead.assignedCounsellor ? 'Reassign' : 'Assign'}
          </Button>
        )}
      </div>

      <AssignLeadModal
        open={showAssign}
        leadIds={[lead._id]}
        currentCounsellorId={lead.assignedCounsellor?._id || null}
        leadCourseId={lead.course?._id || null}
        onClose={() => setShowAssign(false)}
        onAssigned={fetchDetails}
      />

      <LeadProgressPath leadId={leadId} onUpdated={fetchDetails} />
      <AddNoteCard leadId={leadId} canAddNote={canAddNote} onAdded={fetchDetails} />

      <Tab
        tabs={[
          { key: 'info', label: 'Information' },
          { key: 'timeline', label: 'Timeline' },
          { key: 'calls', label: 'Call History' },
          { key: 'notes', label: 'Notes' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'info' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
          {lbl('Full Name', lead.fullName)}
          {lbl('Lead ID', lead.leadId)}
          {lbl('Email', lead.email)}
          {lbl('Mobile', lead.mobile)}
          {lbl('Course', lead.course?.name)}
          {lbl('Category', lead.courseCategory?.name)}
          {lbl('Lead Source', lead.leadSource?.name)}
          {lbl('Qualification', lead.qualification)}
          {lbl('City / State', `${lead.city || ''}, ${lead.state || ''}`)}
          {lbl('Counsellor', lead.assignedCounsellor ? `${lead.assignedCounsellor.firstName} ${lead.assignedCounsellor.lastName}` : 'Unassigned')}
          {lbl('Created', new Date(lead.createdAt).toLocaleDateString())}
          {lbl('Total Calls', lead.totalCalls)}
          {lbl('Total Follow-ups', lead.totalFollowUps)}
          {lbl('Remarks', lead.remarks)}
        </div>
      )}

      {tab === 'timeline' && (
        <div>
          {timeline?.length > 0 ? timeline.map(t => (
            <div key={t._id} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
              <div style={{ width: 8, height: 8, borderRadius: 4, background: '#3B82F6', marginTop: 5, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 13, color: dark ? '#F1F5F9' : '#0F172A', fontWeight: 500 }}>{t.description}</div>
                <div style={{ fontSize: 11, color: dark ? '#64748B' : '#94A3B8' }}>
                  {new Date(t.createdAt).toLocaleString()} • {t.performedBy ? `${t.performedBy.firstName} ${t.performedBy.lastName}` : 'System'}
                </div>
              </div>
            </div>
          )) : <EmptyState icon={Activity} title="No timeline entries yet" />}
        </div>
      )}

      {tab === 'calls' && (
        <div>
          {callHistory?.length > 0 ? callHistory.map(call => (
            <div key={call._id} style={{ display: 'flex', flexDirection: 'column', padding: '12px', borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}`, gap: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 600 }}>
                <Badge color={call.feedback?.color || '#3B82F6'}>{call.feedback?.name || call.feedbackCode}</Badge>
                <span style={{ fontSize: 11, color: dark ? '#64748B' : '#94A3B8' }}>{new Date(call.createdAt).toLocaleString()}</span>
              </div>
              <div style={{ fontSize: 12, color: dark ? '#F1F5F9' : '#0F172A' }}>Duration: {call.callDurationFormatted}</div>
              {call.remarks && <div style={{ fontSize: 12, color: dark ? '#94A3B8' : '#64748B', fontStyle: 'italic' }}>Remarks: {call.remarks}</div>}
              <div style={{ fontSize: 11, color: dark ? '#64748B' : '#94A3B8' }}>Logged by: {call.counsellor ? `${call.counsellor.firstName} ${call.counsellor.lastName}` : '—'}</div>
            </div>
          )) : <EmptyState icon={Phone} title="No calls recorded yet" description="Click the Call button to log a call" />}
        </div>
      )}

      {tab === 'notes' && (
        <div>
          {lead.notes?.length > 0 ? lead.notes.map((note, i) => (
            <div key={i} style={{ padding: '10px 0', borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
              <div style={{ fontSize: 13, color: dark ? '#F1F5F9' : '#0F172A' }}>{note.text}</div>
              <div style={{ fontSize: 11, color: dark ? '#64748B' : '#94A3B8', marginTop: 4 }}>
                {new Date(note.createdAt).toLocaleString()} • {note.createdBy ? `${note.createdBy.firstName} ${note.createdBy.lastName}` : '—'}
              </div>
            </div>
          )) : <EmptyState icon={MessageSquare} title="No notes yet" />}
        </div>
      )}
    </div>
  );
};

export default LeadDetailView;
